import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppSidebar from '../components/layout/AppSidebar';
import Toast from '../components/ui/Toast';
import '../styles/live.css';

const MAX_PROMPT_LENGTH = 2000;

const ProfileSettings: React.FC = () => {
  const navigate = useNavigate();
  const { user, profile, updateProfile } = useAuth();

  const [promptDescription, setPromptDescription] = useState<string>(profile?.prompt_description || '');
  const [saving, setSaving] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  useEffect(() => {
    if (profile) {
      setPromptDescription(profile.prompt_description || '');
    }
  }, [profile]);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile({
        prompt_description: promptDescription.trim() ? promptDescription.trim() : null,
      });
      showToast('Profile saved', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to save profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="aspire-dashboard">
      <AppSidebar />
      <div className="dashboard-content">
        <div className="profile-settings">
          <button className="meeting-back-btn" onClick={() => navigate(-1)}>← Back</button>
          <h1 className="profile-title">User settings</h1>
          <p className="profile-subtitle">
            Signed in as <strong>{user?.display_name}</strong> ({user?.email}). Your custom
            instructions tailor how the AI works for you; they do not change your access level.
          </p>

          <label className="auth-label" htmlFor="profile-prompt">Custom instructions</label>
          <textarea
            id="profile-prompt"
            className="auth-input"
            style={{ minHeight: 120, resize: 'vertical', fontFamily: 'inherit' }}
            value={promptDescription}
            maxLength={MAX_PROMPT_LENGTH}
            onChange={(e) => setPromptDescription(e.target.value.slice(0, MAX_PROMPT_LENGTH))}
            placeholder={'e.g. Keep it short and lead with the key risks and decisions.'}
          />
          <div className="profile-hint">
            Standing instructions the AI follows when tailoring things for you — such as what to
            focus on and how you like information presented.
            {' '}{promptDescription.length}/{MAX_PROMPT_LENGTH}
          </div>

          <div className="profile-actions">
            <button className="meeting-btn meeting-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save profile'}
            </button>
          </div>
        </div>
      </div>

      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={toastVisible}
        onClose={() => setToastVisible(false)}
        duration={3000}
      />
    </div>
  );
};

export default ProfileSettings;
